import dayjs from 'dayjs';
import minMax from 'dayjs/plugin/minMax';
import { ChildDateOfBirth, Child } from '@/types/seekerCC';
import { ChildDOB } from '@care/mfe-data-transfer-common';
import { ChildCareAgeRangeType } from '@/__generated__/globalTypes';
import { ISO_FORMAT } from '../constants';

dayjs.extend(minMax);

export function currentDate() {
  return new Date(Date.now());
}

export function convertDateToISOString(date: Date): string {
  return dayjs(date).format(ISO_FORMAT);
}

export function isValidChildDOB(date: ChildDOB): boolean {
  return dayjs(date).isValid();
}

export function convertDOBToAgeRange(dob: string | null): ChildCareAgeRangeType {
  // For "I'm expecting" case
  if (dob === null) return ChildCareAgeRangeType.NEWBORN;

  const ageInMonths = dayjs(currentDate()).diff(dob, 'month');

  /* eslint-disable no-else-return */
  if (ageInMonths < 7) {
    // 0-6 month
    return ChildCareAgeRangeType.NEWBORN;
  } else if (ageInMonths < 48) {
    // 7 month to 4 years
    return ChildCareAgeRangeType.TODDLER;
  } else if (ageInMonths < 84) {
    // 4-6 years
    return ChildCareAgeRangeType.EARLY_SCHOOL;
  } else if (ageInMonths < 132) {
    // 7-11 years
    return ChildCareAgeRangeType.ELEMENTARY_SCHOOL;
  }
  // 12+ years
  return ChildCareAgeRangeType.TEEN;
}

export const calculateChildrenAges = (childrenDateOfBirth: ChildDateOfBirth[]) => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1; // Months are zero-indexed, so we add 1

  return childrenDateOfBirth.map((child) => {
    const childYear = parseInt(child.year, 10);
    const childMonth = parseInt(child.month, 10);
    let age = currentYear - childYear;

    // If the child hasn't had their birthday yet this year, subtract 1 from the age
    if (currentMonth < childMonth || (currentMonth === childMonth && new Date().getDate() < 1)) {
      age -= 1;
    }

    return age;
  });
};

export const constructChildrenForSeekerCreate = (
  careChildrenDOB: Array<ChildDOB>,
  isExpecting: boolean
) => {
  let careRecipients: Array<Child> | undefined;
  if (careChildrenDOB?.length > 0 || isExpecting) {
    careRecipients = [];
  }
  if (careChildrenDOB?.length > 0) {
    careChildrenDOB.forEach((item) => {
      const child = {
        approximateDoB: item,
        ageRange: convertDOBToAgeRange(item),
      };
      careRecipients?.push(child);
    });
  }
  if (isExpecting) {
    careRecipients?.push({
      ageRange: convertDOBToAgeRange(null),
      approximateDoB: null,
    });
  }

  return careRecipients;
};

export const constructChildDobForGtmEvent = (isExpecting: boolean, childrenDOB: ChildDOB[]) => {
  let childDob: string = '';

  if (isExpecting) {
    childDob = dayjs().add(8, 'month').format('YYYYMMDD');
  } else {
    const parsedDates = childrenDOB.map((date) => dayjs(date));
    const mostRecentDate = dayjs.max(parsedDates);

    if (mostRecentDate) {
      childDob = mostRecentDate.format('YYYYMMDD');
    }
  }

  return childDob;
};

export const getDependentCountForGtmEvent = (isExpecting: boolean, childrenDOB: ChildDOB[]) => {
  return childrenDOB.length + (isExpecting ? 1 : 0);
};

export const convertChildrenDoBsToMonths = (childrenDoBs: ChildDateOfBirth[]): number[] => {
  const ageInMonths: number[] = [];
  const todaysDate = new Date();
  childrenDoBs.forEach((childDoB) => {
    const dob = new Date(Number(childDoB.year), Number(childDoB.month) - 1);
    const numberOfMonths =
      (todaysDate.getFullYear() - dob.getFullYear()) * 12 + todaysDate.getMonth() - dob.getMonth();
    ageInMonths.push(numberOfMonths);
  });

  // ageInMonths array have 1 item, make it like interval
  if (ageInMonths.length === 1) {
    ageInMonths.push(ageInMonths[0]);
  }

  return ageInMonths;
};
